/* eslint-disable react/no-danger */
/* eslint-disable jsx-a11y/anchor-is-valid */

import { React, useEffect, useState } from "react";

import { useFormik } from "formik";

import ClayButton from "@clayui/button";
import ClayForm, { ClayCheckbox, ClayInput } from "@clayui/form";
import ClayIcon from "@clayui/icon";
import ClayModal, { useModal } from "@clayui/modal";

import * as flags from "country-flag-icons/react/3x2";

import * as Yup from "yup";

import { translate as t } from "./i18n";
import ApiClient from "./utils/ApiClient";
import Liferay from "./utils/Liferay";
import CountrySelect from "./components/CountrySelect";

/**
 * Componente principal de registro de usuario.
 *
 * @returns Component react.
 */
function App() {

  // ======================
  // Estados del componente
  // ======================

  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState([]);
  const [spinner, setSpinner] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activeModal, setActiveModal] = useState("TERMS");
  const [acceptMktNotifications, setAcceptMktNotifications] = useState(false);
  const [termsAndConditionsContent, setTermsAndConditionsContent] = useState("");
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);

  // =======================
  // Hook para uso del modal
  // =======================

  const { observer, onOpenChange, open } = useModal();

  // =====================
  // Expresiones regulares
  // =====================

  const REGEXP = {
    ALPHABETICAL: /^[A-Za-zÀ-ÖØ-öø-ÿ\d\s]*$/,
    NUMERIC: /^\d+$/,
    LENGTH: /^\d{10,15}$/,
    PASSWORD: /^(?=.*[A-Z])(?=.*\d)(?=.*[¡!¿?-_+.:,;"@#$%/^&*()[\]{}])(?=.*[a-z]).{8,}$/,
  };

  // ====================
  // Definicion de Formik
  // ====================

  const formik = useFormik({
    initialValues: {
      names: "",
      firstSurname: "",
      secondSurname: "",
      email: "",
      countryCode: "",
      phoneNumber: "",
      password: "",
      passwordConfirmation: "",
      termsAndConditionsRead: false,
    },
    validateOnMount: false,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: Yup.object({
      names: Yup.string()
        .matches(
          REGEXP.ALPHABETICAL,
          t("invalid-characters"),
        )
        .required(t("field-required")),
      firstSurname: Yup.string()
        .matches(
          REGEXP.ALPHABETICAL,
          t("invalid-characters"),
        )
        .required(t("field-required")),
      secondSurname: Yup.string()
        .matches(
          REGEXP.ALPHABETICAL,
          t("invalid-characters"),
        ),
      email: Yup.string()
        .email(t("invalid-email"))
        .required(t("field-required")),
      countryCode: Yup.string().required(),
      phoneNumber: Yup.string()
        .matches(
          REGEXP.NUMERIC,
          t("invalid-phone-number"),
        )
        .matches(
          REGEXP.LENGTH,
          t("invalid-phone-number"),
        )
        .required(t("field-required")),
      password: Yup.string()
        .matches(
          REGEXP.PASSWORD,
          t("invalid-password"),
        )
        .required(t("field-required")),
      passwordConfirmation: Yup.string()
        .oneOf(
          [Yup.ref("password"), null],
          t("passwords-must-match"),
        )
        .required(t("field-required")),
      termsAndConditionsRead: Yup.boolean().oneOf([true]),
    }),
    onSubmit: async (values) => {

      const countryCode = String(values.countryCode).split(" ")[1] || values.countryCode;

      try {

        setSpinner(true);

        const checkEmail = await ApiClient.checkEmail(values.email);

        if (checkEmail) {

          throw String("EMAIL_ALREADY_REGISTERED");

        } else {

          const user = {
            givenName: values.names,
            familyName: values.firstSurname,
            additionalName: values.secondSurname,
            password: values.password,
            emailAddress: values.email,
            currentPassword: values.password,
            languageId: Liferay.ThemeDisplay.getLanguageId(),
            status: "Active",
            userAccountContactInformation: {
              telephones: [
                {
                  phoneNumber: `${countryCode}-${values.phoneNumber}`,
                  primary: true,
                },
              ],
            },
            drNowUser: [{
              name: `${values.names} ${values.firstSurname} ${values.secondSurname}`,
              email: values.email,
            }],
          };

          await ApiClient.createLiferayUser(user);

          formik.resetForm();

          formik.setValues({
            names: "",
            firstSurname: "",
            secondSurname: "",
            email: "",
            phoneNumber: "",
            password: "",
            passwordConfirmation: "",
            termsAndConditionsRead: false,
          });

          setAcceptMktNotifications(false);

          setActiveModal("COMPLETE");

          onOpenChange(true);

        }

      } catch (error) {

        if (error === "EMAIL_ALREADY_REGISTERED") {

          Liferay.Util.openToast({
            title: "",
            message: `
              <h4 class='font-weight-medium m-0'>
                ${t("dr-now-doctor-sign-up-email-registered-title")}
              </h4>
              <span class='text-3'>
                ${t("dr-now-doctor-sign-up-email-registered-message")}
              </span>
            `,
            type: "danger",
            toastProps: {
              hideCloseIcon: true,
            },
          });

        } else {

          Liferay.Util.openToast({
            title: "",
            message: `
              <h4 class='font-weight-medium m-0'>
                ${t("service-error")}
              </h4>
              <span class='text-3'>
                ${t("please-try-again-later")}
              </span>
            `,
            type: "danger",
            toastProps: {
              hideCloseIcon: true,
            },
          });

        }

      } finally {

        setSpinner(false);

      }

    },
  });

  // ========================
  // Funciones del componente
  // ========================

  /**
   * Obtiene la clase a aplicar al elemento del formulario.
   *
   * @param field - Campo a validar.
   *
   * @returns Clase CSS.
   */
  function getFormControlClassName(field) {

    if (!formik.getFieldMeta(field).touched) {

      return "";

    }

    return formik.getFieldMeta(field)?.error ? "has-error" : "is-valid";

  }

  /**
   * Obtiene los catálogos.
   */
  async function getCatalogs() {

    try {

      setLoading(true);

      const response = await ApiClient.getCountriesCatalog();

      const countriesWithoutFlags = response.data?.items;

      const countriesWithFlags = countriesWithoutFlags.map((country) => ({
        ...country,
        flag: flags[country.a2],
      }));

      setCountries(countriesWithFlags);

    } catch {

      Liferay.Util.openToast({
        title: "",
        message: `
          <h4 class='font-weight-medium m-0'>
            ${t("service-error")}
          </h4>
          <span class='text-3'>
            ${t("please-try-again-later")}
          </span>
        `,
        type: "danger",
        toastProps: {
          hideCloseIcon: true,
        },
      });

    } finally {

      setLoading(false);

    }

  }

  /**
   * Obtiene el contenido del modal de términos y condiciones.
   */
  async function getTermsAndConditionsContent() {

    try {

      const response = await ApiClient.getTermsAndConditionsContent();

      setTermsAndConditionsContent(
        response.data,
      );

    } catch (error) {

      Liferay.Util.openToast({
        title: "",
        message: `
          <h4 class='font-weight-medium m-0'>
            ${t("service-error")}
          </h4>
          <span class='text-3'>
            ${t("please-try-again-later")}
          </span>
          `,
        type: "danger",
        toastProps: {
          hideCloseIcon: true,
        },
      });

    }

  }

  /**
   * Valida si el formulario es válido.
   *
   * @returns {boolean} - Indica si el formulario es válido.
   */
  function isFormValid() {

    return (
      formik.isValid
      && !formik.errors.names
      && !formik.errors.firstSurname
      && !formik.errors.email
      && !formik.errors.countryCode
      && !formik.errors.phoneNumber
      && !formik.errors.password
      && !formik.errors.passwordConfirmation
      && formik.values.termsAndConditionsRead
    );

  }

  /**
   * Renderiza el contenido del modal de términos y condiciones.
   *
   * @returns Contenido del modal de términos y condiciones.
   */
  function renderTermsAndConditionsModal() {

    return termsAndConditionsContent && (
      <>
        <div className="row mb-2 mb-md-4 text-center">
          <div
            className="col"
            dangerouslySetInnerHTML={{
              __html: termsAndConditionsContent,
            }}
          />
        </div>

        <div className="row justify-content-center mb-2 mb-md-0">
          <div className="col col-lg-auto">

            <ClayButton
              displayType="primary"
              size="lg"
              block
              onClick={() => {

                formik.setFieldValue("termsAndConditionsRead", true);
                onOpenChange(false);

              }}
            >
              {t("accept")}
            </ClayButton>

          </div>
        </div>

      </>
    );

  }

  /**
   * Carga la estructura del modal.
   *
   * @returns Estructura del modal.
   */
  function renderModal() {

    if (!open) {

      return false;

    }

    return (

      <ClayModal
        center
        disableAutoClose
        observer={observer}
        size={activeModal === "TERMS" ? "full-screen" : "lg"}
        className={activeModal === "TERMS" && "modal-height-lg"}
      >

        <ClayModal.Header className="text-right" withTitle={false}>

          {activeModal === "COMPLETE"
            && (
              <div className="modal-title text-center text-5 font-weight-semi-bold">
                {t("sign-up-completed-message")}
              </div>
            )}

          {activeModal === "TERMS" && (

            <button
              aria-label="close"
              type="button"
              className="close"
              onClick={() => onOpenChange(false)}
            >

              <ClayIcon
                spritemap={Liferay.Icons.spritemap}
                symbol="dr-now-times"
              />

            </button>

          )}

        </ClayModal.Header>

        <ClayModal.Body>

          {activeModal === "TERMS" ? renderTermsAndConditionsModal()
            : (

              <div className="row justify-content-center">
                <div className="col-auto">

                  <button
                    className="btn btn-primary btn-lg"
                    type="button"
                    onClick={() => {

                      Liferay.SPA.app.navigate("/web/dr-now-public/home");

                      onOpenChange(false);

                    }}
                  >

                    {t("go-to-home")}

                  </button>

                </div>
              </div>

            )}

        </ClayModal.Body>

      </ClayModal>
    );

  }

  /**
   * Método que muestra el mensaje de error de un campo.
   *
   * @param fieldName - Nombre del campo.
   *
   * @returns Componente con el mensaje de error del campo.
   */
  function renderFieldErrorMessage(fieldName) {

    if (formik.getFieldMeta(fieldName).touched && formik.errors?.[fieldName]) {

      return (
        <div className="form-feedback-item">

          <ClayIcon
            className="text-2 text-danger mr-1"
            spritemap={Liferay.Icons.spritemap}
            symbol="dr-now-bell"
          />

          {formik.errors[fieldName]}
        </div>
      );

    }

    return null;

  }

  /**
   * Renderiza el formulario de datos del usuario.
   *
   * @returns Component con Formulario.
   */
  function renderUserForm() {

    return (
      <>
        <div className="row">
          <div className="col-12">

            <ClayForm.Group className={`${getFormControlClassName("names")}`}>

              <label className="form-control-label" htmlFor="names">
                {t("names")}
                <span className="text-danger">*</span>
              </label>

              <ClayInput
                id="names"
                name="names"
                value={formik.values.names}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={t("names-placeholder")}
                autoComplete="off"
                type="text"
              />

              <div className="form-feedback-group">

                {renderFieldErrorMessage("names")}

                <div className="form-text d-none d-sm-block">
                  {t("enter-two-or-more-names")}
                </div>

              </div>

            </ClayForm.Group>

          </div>
        </div>

        <div className="row">

          <div className="col-12 col-md-6">

            <ClayForm.Group className={`${getFormControlClassName("firstSurname")}`}>

              <label className="form-control-label" htmlFor="firstSurname">

                {t("first-surname")}

                <span className="text-danger">*</span>

              </label>

              <ClayInput
                id="firstSurname"
                name="firstSurname"
                value={formik.values.firstSurname}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={t("surname-placeholder")}
                autoComplete="off"
                type="text"
              />

              <div className="form-feedback-group">

                {renderFieldErrorMessage("firstSurname")}

                <div className="form-text d-none d-md-block">
                  {t("enter-your-first-surname")}
                </div>

              </div>

            </ClayForm.Group>

          </div>

          <div className="col-12 col-md-6">

            <ClayForm.Group className={`${getFormControlClassName("secondSurname")}`}>

              <label className="form-control-label" htmlFor="secondSurname">
                {t("second-surname")}
              </label>

              <ClayInput
                id="secondSurname"
                name="secondSurname"
                value={formik.values.secondSurname}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={t("surname-placeholder")}
                autoComplete="off"
                type="text"
              />

              <div className="form-feedback-group">

                {renderFieldErrorMessage("secondSurname")}

                <div className="form-text d-none d-md-block">
                  {t("enter-your-second-surname")}
                </div>

              </div>

            </ClayForm.Group>

          </div>

        </div>

        <div className="row">

          <div className="col-12 col-md-6 mb-3">

            <ClayForm.Group className={`${getFormControlClassName("email")}`}>

              <label className="form-control-label" htmlFor="email">

                {t("email")}

                <span className="text-danger">*</span>

              </label>

              <ClayInput
                id="email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={t("email-placeholder")}
                autoComplete="off"
                type="text"
              />

              <div className="form-feedback-group">

                {renderFieldErrorMessage("email")}

                <div className="form-text d-none d-md-block">
                  {t("enter-your-work-email")}
                </div>

              </div>

            </ClayForm.Group>

          </div>

          <div className="md-6 col">

            <ClayForm.Group className={`${getFormControlClassName("countryCode")}`}>

              <label className="form-control-label" htmlFor="countryCode">
                {t("country-code")}
                <span className="text-danger">*</span>
              </label>

              <CountrySelect formik={formik} countries={countries} loading={loading} />

              <div className="form-feedback-group">

                <div className="form-text d-none d-md-block">
                  {t("country")}
                </div>

              </div>

            </ClayForm.Group>

          </div>

          <div className="md-6 col">

            <ClayForm.Group className={`${getFormControlClassName("phoneNumber")}`}>

              <label className="form-control-label" htmlFor="phoneNumber">

                {t("phone-number")}

                <span className="text-danger">*</span>

              </label>

              <ClayInput
                id="phoneNumber"
                name="phoneNumber"
                value={formik.values.phoneNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={t("phone-number-placeholder")}
                autoComplete="off"
                type="text"
              />

              <div className="form-feedback-group">

                {renderFieldErrorMessage("phoneNumber")}

                <div className="form-text d-none d-md-block">
                  {t("enter-your-phone-number")}
                </div>

              </div>

            </ClayForm.Group>

          </div>

        </div>
      </>
    );

  }

  /**
   * Renderiza el formulario de contraseña.
   *
   * @returns Componente React.
   */
  function renderPasswordForm() {

    return (
      <>

        <div className="row mb-3">
          <div className="col text-center">

            <span className="text-5 font-weight-semi-bold text-primary">
              {t("password")}
            </span>

          </div>
        </div>

        <div className="row mb-3">
          <div className="col text-center">

            <span className="text-gray-900">
              {t("enter-a-safe-password-msg")}
            </span>

          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-md-8">

            <ClayForm.Group className={getFormControlClassName("password")}>

              <label className="form-control-label" htmlFor="password">

                {t("password")}

                <span className="text-danger">
                  *
                </span>

              </label>

              <div className="position-relative">

                <ClayInput
                  id="password"
                  name="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={t("password-placeholder")}
                  autoComplete="off"
                  type={showPassword ? "text" : "password"}
                  className="w-100"
                />

                <ClayButton
                  aria-label="showPassword"
                  className="position-absolute"
                  style={{
                    top: "50%",
                    right: "10px",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                  }}
                  displayType="unstyled"
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                >

                  <ClayIcon
                    className="text-primary text-4"
                    symbol={showPassword ? "dr-now-hide" : "dr-now-show"}
                    spritemap={Liferay.Icons.spritemap}
                  />

                </ClayButton>

              </div>

              <div className="form-feedback-group">

                {renderFieldErrorMessage("password")}

                <div className="form-text">
                  {`* ${t("at-least-eight-characters")}`}
                </div>

                <div className="form-text mt-0">
                  {`* ${t("at-least-one-capital-letter")}`}
                </div>

                <div className="form-text mt-0">
                  {`* ${t("at-least-one-number")}`}
                </div>

                <div className="form-text mt-0">
                  {`* ${t("at-least-one-special-character")}`}
                </div>

              </div>

            </ClayForm.Group>

          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-md-8">

            <ClayForm.Group className={getFormControlClassName("passwordConfirmation")}>

              <label className="form-control-label" htmlFor="passwordConfirmation">

                {t("password-confirmation")}

                <span className="text-danger">
                  *
                </span>

              </label>

              <div className="position-relative">

                <ClayInput
                  id="passwordConfirmation"
                  name="passwordConfirmation"
                  value={formik.values.passwordConfirmation}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={t("password-confirmation-placeholder")}
                  autoComplete="off"
                  type={showPasswordConfirmation ? "text" : "password"}
                  className="w-100"
                />

                <ClayButton
                  aria-label="passwordConfirmation"
                  className="position-absolute"
                  style={{
                    top: "50%",
                    right: "10px",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                  }}
                  displayType="unstyled"
                  type="button"
                  onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                >

                  <ClayIcon
                    className="text-primary text-4"
                    symbol={showPasswordConfirmation ? "dr-now-hide" : "dr-now-show"}
                    spritemap={Liferay.Icons.spritemap}
                  />

                </ClayButton>

              </div>

              <div className="form-feedback-group">

                {renderFieldErrorMessage("passwordConfirmation")}

                <div className="form-text">
                  {`* ${t("password-confirmation-msg")}`}
                </div>

              </div>

            </ClayForm.Group>

          </div>
        </div>
      </>
    );

  }

  /**
   * Renderiza los checkboxes de la sección de términos y condiciones.
   *
   * @returns Componente React.
   */
  function renderCheckboxesSection() {

    return (
      <>
        <div className="row">
          <div className="col">

            <ClayCheckbox
              id="termsAndConditionsRead"
              name="termsAndConditionsRead"
              checked={formik.values.termsAndConditionsRead}
              value={formik.values.termsAndConditionsRead}
              onChange={formik.handleChange}
              inline
              label={(
                <>
                  <span>
                    {`${t("read-and-accept")}`}
                  </span>

                  <a
                    href="#"
                    className="ml-1 mb-2 p-0 text-2 font-weight-bold"
                    onClick={(e) => {

                      e.preventDefault();

                      setActiveModal("TERMS");
                      onOpenChange(true);

                    }}
                  >

                    {`${t("terms-and-conditions")}*`}

                  </a>

                </>
              )}
            />

          </div>
        </div>

        <div className="row">
          <div className="col">

            <ClayCheckbox
              id="acceptMktNotifications"
              name="acceptMktNotifications"
              checked={acceptMktNotifications}
              onChange={() => setAcceptMktNotifications(!acceptMktNotifications)}
              label={(
                <span>

                  {t("marketing-notifications-agree")}

                  <span className="text-muted">
                    {` (${t("optional")})`}
                  </span>

                </span>
              )}

            />

          </div>
        </div>
      </>
    );

  }

  /**
   * Renderiza la sección de botones.
   *
   * @returns Componente React.
   */
  function renderButtonsSection() {

    return (
      <>

        <div className="row mb-2">
          <div className="col text-center">

            <span className="text-2">
              {`*${t("previous-fields-are-mandatory")}`}
            </span>

          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-sm col-md-auto">

            <ClayButton
              displayType="primary"
              type="submit"
              size="lg"
              disabled={!isFormValid() || spinner || loading}
              block
            >
              {!spinner ? t("continue")
                : (
                  <>

                    <span className="mr-2">
                      {t("processing")}
                    </span>

                    <ClayIcon
                      className="spinning-animation"
                      spritemap={Liferay.Icons.spritemap}
                      symbol="reload"
                    />

                  </>
                )}
            </ClayButton>

          </div>
        </div>

      </>
    );

  }

  // ===============================
  // Función de carga del componente
  // ===============================

  useEffect(() => {

    if (!Liferay || !Liferay.SPA) {

      return;

    }

    getTermsAndConditionsContent();

    getCatalogs();

  }, []);

  // =======================
  // Template del componente
  // =======================

  return (
    <div className="container-fluid px-lg-6 px-xl-8 px-4">

      <div className="row mb-2">
        <div className="col text-center">

          <h3 className="font-weight-semi-bold text-primary">
            {t("user-sign-up")}
          </h3>

        </div>
      </div>

      <div className="row mb-2">
        <div className="col text-center font-weight-light">

          <p className="text-gray-900">

            {t("please-fill-your-data")}

            <span className="font-weight-semi-bold">
              {t("dr-now")}
            </span>

          </p>

        </div>
      </div>

      <form onSubmit={formik.handleSubmit}>

        <div className="row mb-3 mb-md-2">
          <div className="col">{renderUserForm()}</div>
        </div>

        <div className="row mb-4">
          <div className="col">{renderPasswordForm()}</div>
        </div>

        <hr />

        <div className="row my-4">
          <div className="col">{renderCheckboxesSection()}</div>
        </div>

        <hr />

        <div className="row mb-4">
          <div className="col">{renderButtonsSection()}</div>
        </div>

      </form>

      {renderModal()}

    </div>
  );

}

export default App;
